import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

export const workflowTemplatesApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "workflowTemplatesApi",
    baseUrl: "/api/workflow-templates",
  }),
  reducerPath: "workflowTemplatesApi",
  tagTypes: ["WorkflowTemplates", "WorkflowTemplate", "WorkflowTemplateStep"],

  endpoints: (builder) => ({
    getWorkflowTemplates: builder.query<
      any,
      {
        type?: string;
        include?: string;
        search?: string;
        nextId?: string;
        limit?: number;
        shareLevel?: string;
      }
    >({
      query: (args) => {
        // Build query parameters dynamically
        const queryParams = new URLSearchParams();
        // Append parameters if they exist
        if (args.type) queryParams.append("type", args.type); //support for type=ALL
        if (args.search) queryParams.append("query", args.search);
        if (args.include) queryParams.append("include", args.include);
        if (args.limit) queryParams.append("limit", args.limit.toString());
        if (args.nextId) queryParams.append("nextId", args.nextId);
        if (args.shareLevel) queryParams.append("shareLevel", args.shareLevel);
        return `?${queryParams.toString()}`;
      },
      providesTags: [{ type: "WorkflowTemplates" }],
    }),
    getWorkflowTemplatesById: builder.query<
      any,
      { id?: string; include?: string }
    >({
      query: (args) => {
        const queryParams = args.include ? `?include=${args.include}` : "";
        return `/${args.id}${queryParams}`;
      },
      keepUnusedDataFor: 0,
      providesTags: [{ type: "WorkflowTemplate" }],
    }),

    getWorkflowTemplateStepById: builder.query<
      any,
      { workflowId: string; stepId: string }
    >({
      query: (args) => {
        return `/${args.workflowId}/step-templates/${args.stepId}`;
      },
      providesTags: [{ type: "WorkflowTemplateStep" }],
    }),
    createWorkflowTemplate: builder.mutation<
      any,
      { name: string; active: boolean; type?: string }
    >({
      query: ({ name, active, type }) => ({
        url: "/",
        method: "POST",
        body: { name, active, ...(type ? { type } : {}) },
      }),
    }),
    updateWorkflowTemplate: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: "PUT",
        body: data,
      }),
    }),
    createStepTemplate: builder.mutation<any, { id: string; data: any }>({
      query: ({ id, data }) => ({
        url: `/${id}/step-templates`,
        method: "POST",
        body: data,
      }),
    }),
    updateWorkflowStepTemplate: builder.mutation<
      any,
      { id: string; data: any }
    >({
      query: ({ id, data }) => ({
        url: `/${id}/step-templates/${data.id}`,
        method: "PUT",
        body: data,
      }),
    }),
    deleteWorkflowTemplate: builder.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
    }),
    // Bulk reorder a template's step templates in one atomic request — parity
    // with the deal-workflow reorder endpoint.
    reorderStepTemplates: builder.mutation<
      { message: string },
      { id: string; steps: { id: string; order: number }[] }
    >({
      query: ({ id, steps }) => ({
        url: `/${id}/step-templates`,
        method: "PATCH",
        body: { steps },
      }),
      invalidatesTags: ["WorkflowTemplate"],
    }),
  }),
});

export const {
  useGetWorkflowTemplatesQuery,
  useGetWorkflowTemplatesByIdQuery,
  useGetWorkflowTemplateStepByIdQuery,
  useCreateWorkflowTemplateMutation,
  useUpdateWorkflowTemplateMutation,
  useCreateStepTemplateMutation,
  useUpdateWorkflowStepTemplateMutation,
  useDeleteWorkflowTemplateMutation,
  useReorderStepTemplatesMutation,
} = workflowTemplatesApi;
